import { Bin, Item, Packer } from "@owens3364/3d-bin-packing";

/**
 * Get the shipping price.
 * This function doesn't allow to pack over one box (Only one box packaging is allowed).
 * It should change the function and check process by size & weight if it needs to allow over one box packaging.
 *
 * @param items Selected products
 * @param parcelBoxes Standard parcel box info (by CMS > CountryCode > ShippingRate)
 *                    parcel boxes can be undefined before loading
 */
export const getShippingRate = (items: any[], parcelBoxes?: ParcelBox[]) => {
    let totalWeight = 0;

    if (parcelBoxes?.length && items.length) {
        for (const parcelBox of parcelBoxes) {
            const box = new Bin('parcel-box', parcelBox.maxWidth, parcelBox.maxHeight, parcelBox.maxDepth);
            const packer = new Packer();
            packer.addBin(box);

            for (const item of items) {
                for (let i = 0; i < item.quantity; i++) {
                    packer.addItem(new Item(`item-${item.id}-${i}`, item.width, item.height, item.depth));
                    totalWeight += item.weight;
                }
            }

            packer.pack();

            if (!packer.unfitItems.length) {
                return getShippingAmount(parcelBoxes, parcelBox, totalWeight);
            }
        }

        // Has not the fitted box
        return getShippingAmount(parcelBoxes, parcelBoxes[parcelBoxes.length - 1], totalWeight);
    }

    // [Error] It means admin didn't set any standard parcel boxes on CMS.
    return -1;
};

/**
 * Get the price of the fitted box. Moreover, if the weight is over despite finding the fitted box, it finds the
 * closest weight's price for the over weight.
 *
 * @param parcelBoxes Standard parcel box info (by CMS > CountryCode > ShippingRate)
 * @param currentBox The fitted box of selected items
 * @param totalWeight The total weight of selected items
 */
const getShippingAmount = (parcelBoxes: ParcelBox[], currentBox: ParcelBox, totalWeight: number) => {
    let parcelBoxObject;
    let shippingPrice = 0;

    const sameSizeBoxes = parcelBoxes
        .filter((parcelBox: ParcelBox) => (
            parcelBox.maxDepth === currentBox.maxDepth &&
            parcelBox.maxWidth === currentBox.maxWidth &&
            parcelBox.maxHeight === currentBox.maxHeight
        ));

    const allowedSizeBox = sameSizeBoxes.filter(parcelBox => totalWeight > parcelBox.maxWeight);

    if (!allowedSizeBox.length) {
        parcelBoxObject = sameSizeBoxes.reduce((max, current) => (current.maxWeight > max.maxWeight ? current : max));
        shippingPrice += parcelBoxObject.price;

    // Over weight
    } else {
        const allowedBox = sameSizeBoxes.reduce(
            (closest, current) => {
                return Math.abs(current.maxWeight - totalWeight) < Math.abs(closest.maxWeight - totalWeight) ? current : closest
            }
        );

        // Add the shipping price of the packed box size
        shippingPrice += allowedBox.price;

        // Remaining weight after packing
        let overWeight = totalWeight - allowedBox.maxWeight;

        // Over weight: find the price of the closest weight.
        while (overWeight > 0) {
            const cloneOverWeight = overWeight;

            const extraBox = sameSizeBoxes.reduce(
                (closest, current) => {
                    const closestDiff = Math.abs(cloneOverWeight - closest.maxWeight);
                    const currentDiff = Math.abs(cloneOverWeight - current.maxWeight);

                    if (currentDiff < closestDiff) {
                        return current;
                    }

                    // If the weight gap is the same, the price of the light total size has a priority.
                    if (currentDiff === closestDiff) {
                        const currentSizeSum = current.maxDepth + current.maxWidth + current.maxHeight;
                        const closestSizeSum = closest.maxDepth + closest.maxWidth + closest.maxHeight;

                        if (currentSizeSum < closestSizeSum) {
                            return current;
                        }
                    }
                    return closest;
                }
            );

            if (extraBox) {
                shippingPrice += extraBox.price;
                overWeight -= extraBox.maxWeight;
            } else {
                break;
            }
        }
    }

    return shippingPrice;
}

/**
 * Make specific boxes by a product title, not a general box.
 * @param allItemsToPack
 */
const getSpecificProductBoxes = (allItemsToPack: ExpandedCartProduct[]) => {
    const newItemsToPack: ExpandedCartProduct[] = [];
    let finalAllItemsToPack: ExpandedCartProduct[] = [];
    finalAllItemsToPack.push(...allItemsToPack);

    SPECIFIC_PRODUCT_BOXES.forEach((specificProductBox: SpecificProductBox) => {
        let allocatedNumber = 0;
        let allocatedIds: string[] = [];

        finalAllItemsToPack.forEach((item: ExpandedCartProduct) => {
            if (specificProductBox.productType === item.productType) {
                allocatedNumber++;
                allocatedIds.push(item.id);

                // Create a specific box.
                if (allocatedNumber === specificProductBox.quantity) {
                    newItemsToPack.push({
                        id: allocatedIds.join(','),
                        productType: item.productType,
                        width: specificProductBox.widthCms,
                        height: specificProductBox.heightCms,
                        depth: specificProductBox.depthCms,
                        weight: item.weight * specificProductBox.quantity,
                        quantity: 1,
                    });

                    // Remove the packed items.
                    finalAllItemsToPack = finalAllItemsToPack.filter((item: ExpandedCartProduct) => (
                        !allocatedIds.includes(item.id)
                    ));

                    allocatedNumber = 0;
                    allocatedIds = [];
                }
            }
        });
    });

    if (newItemsToPack.length > 0) {
        finalAllItemsToPack.push(...newItemsToPack);
        return finalAllItemsToPack;
    }

    return allItemsToPack;
};

export const SPECIFIC_PRODUCT_BOXES: SpecificProductBox[] = [
    {
        productType: "sentence-train-teacher-edition",
        quantity: 3,
        widthCms: 48,
        heightCms: 16,
        depthCms: 32,
    }
];

export const NZ_POST_BOXES: NzPostBox[] = [
    {
        name: "size-1",
        widthCms: 23.5,
        heightCms: 7,
        depthCms: 16.5,
    },
    {
        name: "size-2",
        widthCms: 25,
        heightCms: 17,
        depthCms: 18.5,
    },
    {
        name: "size-3",
        widthCms: 35,
        heightCms: 20,
        depthCms: 26.5,
    },
    {
        name: "poster-box",
        widthCms: 63,
        heightCms: 4,
        depthCms: 42.5,
    },
    {
        name: "size-4",
        widthCms: 38,
        heightCms: 21.6,
        depthCms: 30,
    },
    {
        name: "size-5",
        widthCms: 50.7,
        heightCms: 21,
        depthCms: 31,
    },
    {
        name: "size-6",
        widthCms: 48,
        heightCms: 16,
        depthCms: 32,
    },
].sort((a: NzPostBox, b: NzPostBox) => b.widthCms * b.heightCms * b.depthCms - a.widthCms * a.heightCms * a.depthCms);

export const runPackingProcess = (allItemsToPack: ExpandedCartProduct[]) => {
    const finalAllItemsToPack = getSpecificProductBoxes(allItemsToPack);

    // Ensure each item has a unique ID for tracking. We can have the same product ID with quantity > 1.
    let itemsWithTrackingId = finalAllItemsToPack.map((item, index) => ({
        ...item,
        trackingId: item.id + "_" + index,
    }));

    itemsWithTrackingId.sort(
        (a, b) => b.width * b.height * b.depth - a.width * a.height * a.depth
    );

    let unpackedItems = [...itemsWithTrackingId];
    const packedArrangements: PackedBoxes[] = [];

    while (unpackedItems.length > 0) {
        let boxFilledInThisIteration = false;

        for (const boxType of NZ_POST_BOXES) {
            const packer = new Packer();
            const bin = new Bin(
                boxType.name,
                boxType.widthCms,
                boxType.heightCms,
                boxType.depthCms,
            );
            packer.addBin(bin);

            unpackedItems.forEach((itemData) => {
                packer.addItem(
                    new Item(
                        itemData.trackingId, // Use the unique trackingId as the item name for the library
                        itemData.width,
                        itemData.height,
                        itemData.depth,
                    )
                );
            });

            packer.pack();

            // Access packed items from the bin instance.
            if (bin.items && bin.items.length > 0) {
                // Their 'name' property will be the 'trackingId' we passed.
                const packedItemTrackingIds = bin.items.map(
                    (packedLibItem) => packedLibItem.name
                );

                // Filter out original item data based on trackingId
                const itemsActuallyPackedInThisBin = itemsWithTrackingId.filter(
                    (originalItem) =>
                        packedItemTrackingIds.includes(originalItem.trackingId)
                );

                const packedItems = itemsActuallyPackedInThisBin.filter((item) =>
                    packedItemTrackingIds.includes(item.trackingId)
                );

                packedArrangements.push({
                    boxType: { ...boxType },
                    // Store a copy of the original item data, not the library's Item instances
                    items: packedItems,
                    totalWeight: packedItems.reduce((a: number, b) => a + b.weight, 0)
                });

                // Update unpackedItems: Remove items whose trackingId is in packedItemTrackingIds
                unpackedItems = unpackedItems.filter(
                    (item) => !packedItemTrackingIds.includes(item.trackingId)
                );

                boxFilledInThisIteration = true;
                break; // Move to the next physical box (i.e., start a new iteration of the while loop)
            }
        }

        if (!boxFilledInThisIteration && unpackedItems.length > 0) {
            // Add remaining items to the biggest box as we cannot pack them into any other box.
            // Forcing a break to prevent infinite loops if items genuinely can't fit.
            // We only will force 10 items to fit into the biggest box.
            const chunkSize = 10;
            for (let i = 0; i < unpackedItems.length; i+= chunkSize) {
                const chunk = unpackedItems.slice(i, i + chunkSize);
                packedArrangements.push({
                    boxType: NZ_POST_BOXES[0],
                    items: [...chunk],
                    totalWeight: chunk.reduce((a: number, b) => {
                        return a + (b.weight || 0);
                    }, 0)
                });
            }
            unpackedItems = []; // Clear to stop loop
            break;
        }
    }

    return packedArrangements;
};